import { createElement } from 'react';
import {
  createAiSummaryFieldReadModule,
  type AiSummaryEmptyOutcomeLabels
} from './ai-summary/ai-summary-field-read-module.ts';
import { AiSummaryValueView } from './ai-summary/ai-summary-value-view.tsx';
import {
  createAttachmentFieldReadModule,
  type AttachmentReadPolicy,
  type AttachmentReadRenderer
} from './attachment/attachment-field-read-module.ts';
import {
  createBacklinkFieldReadModule,
  type BacklinkReadPolicy
} from './backlink/backlink-field-read-module.ts';
import { BacklinkValueView } from './backlink/backlink-value-view.tsx';
import {
  createCheckboxFieldReadModule,
  type CheckboxReadPolicy
} from './checkbox/checkbox-field-read-module.ts';

import { createDateFieldReadModule } from './date/date-field-read-module.ts';
import {
  createEntryDetailsFieldReadModule,
  type EntryDetailsReadPolicy
} from './entry-details/entry-details-field-read-module.ts';
import {
  createEntryLinkFieldReadModule,
  type EntryLinkReadPolicy
} from './entry-link/entry-link-field-read-module.ts';
import { EntryLinkValueView } from './entry-link/entry-link-value-view.tsx';
import { createFieldReadRegistry, type FieldReadRegistry } from './field-read-module.ts';
import { createHyperlinkFieldReadModule } from './hyperlink/hyperlink-field-read-module.ts';
import { HyperlinkValueView } from './hyperlink/hyperlink-value-view.tsx';
import {
  createJiraIssueDetailsFieldReadModule,
  type JiraIssueDetailsReadPolicy
} from './jira-issue-details/jira-issue-details-field-read-module.ts';
import {
  createJiraIssueFieldReadModule,
  type JiraIssueReadPolicy
} from './jira-issue/jira-issue-field-read-module.ts';
import { JiraIssueValueView } from './jira-issue/jira-issue-value-view.tsx';
import { createNumberFieldReadModule } from './number/number-field-read-module.ts';
import {
  createPageDetailsFieldReadModule,
  type PageDetailsReadPolicy
} from './page-details/page-details-field-read-module.ts';
import {
  createPageLabelsFieldReadModule,
  type PageLabelsReadPolicy
} from './page-labels/page-labels-field-read-module.ts';
import {
  createPageLinkFieldReadModule,
  type PageLinkReadPolicy
} from './page-link/page-link-field-read-module.ts';
import { PageLinkValueView } from './page-link/page-link-value-view.tsx';
import {
  createPageStatusFieldReadModule,
  type PageStatusReadPolicy
} from './page-status/page-status-field-read-module.ts';
import {
  createRichTextFieldReadModule,
  type RichTextReadRenderer
} from './rich-text/rich-text-field-read-module.ts';
import { selectFieldReadModule } from './select/select-field-read-module.ts';
import {
  createUserFieldReadModule,
  type UserReadPolicy,
  type UserReadRenderer
} from './user/user-field-read-module.ts';

/**
 * Immutable application policy captured before field modules are compiled.
 */
export type FieldReadPolicy = {
  readonly locale: string;
  readonly jiraIssueDetails?: JiraIssueDetailsReadPolicy;
  readonly entryDetails?: EntryDetailsReadPolicy;
  readonly backlink?: BacklinkReadPolicy;
  readonly pageStatus?: PageStatusReadPolicy;
  readonly pageLabels?: PageLabelsReadPolicy;
  readonly pageDetails?: PageDetailsReadPolicy;
  readonly entryLink?: EntryLinkReadPolicy;
  readonly jiraIssue?: JiraIssueReadPolicy;
  readonly attachment?: AttachmentReadPolicy;
  readonly renderAttachment?: AttachmentReadRenderer;
  readonly user?: UserReadPolicy;
  readonly renderUser?: UserReadRenderer;
  readonly pageLink?: PageLinkReadPolicy;
  readonly checkbox: CheckboxReadPolicy;
  readonly aiSummaryEmptyOutcomeLabels?: AiSummaryEmptyOutcomeLabels;
  readonly isMobile?: boolean;
  readonly renderRichText?: RichTextReadRenderer;
  /**
   * V1 Date uses the user/override locale, which can differ from the Intl provider.
   */
  readonly dateLocale: string;
  /**
   * Passive application policy; field modules never evaluate experiments or fire exposure.
   */
  readonly dateFieldSettingsEnabled: boolean;
};

/**
 * Creates the explicit production registry; unsupported types render empty.
 */
export function createDefaultFieldReadRegistry(
  policy: FieldReadPolicy
): FieldReadRegistry {
  return createFieldReadRegistry([
    selectFieldReadModule,
    createJiraIssueDetailsFieldReadModule(policy.jiraIssueDetails),
    createEntryDetailsFieldReadModule(policy.entryDetails),
    createBacklinkFieldReadModule({
      brokenLabel: '',
      restrictedLabel: '',
      unnamedLabel: '',
      ...policy.backlink,
      render: (content) =>
        createElement(BacklinkValueView, { content, isMobile: policy.isMobile })
    }),
    createPageStatusFieldReadModule(policy.pageStatus),
    createPageLabelsFieldReadModule(policy.pageLabels),
    createPageDetailsFieldReadModule(policy.pageDetails),
    createEntryLinkFieldReadModule({
      brokenLabel: '',
      unnamedLabel: '',
      ...policy.entryLink,
      render: (content) =>
        createElement(EntryLinkValueView, { content, isMobile: policy.isMobile })
    }),
    createJiraIssueFieldReadModule({
      ...policy.jiraIssue,
      render: (content) =>
        createElement(JiraIssueValueView, {
          content,
          showDisabledValue: policy.isMobile,
          authenticationLabel: policy.jiraIssue?.authenticationLabel
        })
    }),
    createAttachmentFieldReadModule({
      ...policy.attachment,
      render: policy.renderAttachment
    }),
    createUserFieldReadModule({ ...policy.user, render: policy.renderUser }),
    createPageLinkFieldReadModule({
      baseUrl: '',
      ...policy.pageLink,
      render: (content) =>
        createElement(PageLinkValueView, { content, showDisabledValue: policy.isMobile })
    }),
    createCheckboxFieldReadModule(policy.checkbox),
    createAiSummaryFieldReadModule({
      emptyOutcomeLabels: policy.aiSummaryEmptyOutcomeLabels,
      render: (content) =>
        createElement(AiSummaryValueView, {
          content,
          renderRichText: policy.renderRichText
        })
    }),
    createRichTextFieldReadModule({ render: policy.renderRichText }),
    createNumberFieldReadModule({ locale: policy.locale }),
    createDateFieldReadModule({
      locale: policy.dateLocale,
      dateFieldSettingsEnabled: policy.dateFieldSettingsEnabled
    }),
    createHyperlinkFieldReadModule({
      render: (content) =>
        createElement(HyperlinkValueView, { content, showDisabledValue: policy.isMobile })
    })
  ]);
}
